/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Upload } from '@mui/icons-material';
import { HeaderEnum, VitalsInterface } from 'medicalsystem-shared';
import { useTranslation } from 'react-i18next';
import { CoreAPI_URLs } from 'constants/http.const';
import useHttp from 'hooks/http.hook';
import Button from 'components/generals/Button';
import { useDispatch } from "react-redux";
import EventSlice, { MessageType } from 'store/slices/event.slice';

interface PDFVitalsUploaderProps {
  onDataExtracted: (vitalsData: Partial<VitalsInterface>, pdfFile: File) => void;
  disabled?: boolean;
  appointmentId: string;
}

const PDFVitalsUploader: React.FC<PDFVitalsUploaderProps> = ({
  onDataExtracted,
  disabled = false,
  appointmentId,
}) => {
  const [isLoading, , sendRequest] = useHttp();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const { addErrorMessage, addSuccessMessage } =EventSlice.actions;

  const uploadPdfToServer = async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('appointmentId', appointmentId);
    await sendRequest<VitalsInterface>(
      CoreAPI_URLs.VITALS_UPLOAD_PDF,
      (response: VitalsInterface) => {
        onDataExtracted(response, file);
      },
      "POST",
      formData,
      [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }],
      {
        applyErrorFn: (errorMessage: string) => {
          setError(errorMessage);
        },
        successMessage: t('PDFVitalsUploader.success'),
      }
    );
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      dispatch(addErrorMessage({ message: t('PDFVitalsUploader.errorInvalidFileType'), messageType: MessageType.ERROR, id: crypto.randomUUID() }));
      return;
    }

    if (!appointmentId) {
      dispatch(addErrorMessage({ message: t('PDFVitalsUploader.errorAppointmentIdRequired'), messageType: MessageType.ERROR, id: crypto.randomUUID() }));
      return;
    }

    try {
      await uploadPdfToServer(file);
    } catch (err) {
      dispatch(addErrorMessage({ message: t('PDFVitalsUploader.error'), messageType: MessageType.ERROR, id: crypto.randomUUID() }));
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Box className="pdf-vitals-uploader">
      <input
        accept="application/pdf"
        className="hidden-input"
        id="pdf-upload-input"
        type="file"
        onChange={handleFileUpload}
        disabled={disabled || isLoading}
        ref={fileInputRef}
      />
      <label htmlFor="pdf-upload-input">
        <Button
          variant="contained"
          component="span"
          color="info"
          startIcon={isLoading ? <CircularProgress/> : <Upload />}
          disabled={disabled || isLoading}
          className="upload-button"
        >
          {isLoading ? t('PDFVitalsUploader.uploading') : t('PDFVitalsUploader.uploadPdf')}

        </Button>
      </label>
      <Typography variant="caption" className="upload-description">
        {t('PDFVitalsUploader.uploadPdfDescription')}
      </Typography>
    </Box>
  );
};

export default PDFVitalsUploader;